/**
 * 행발(행동특성 및 종합의견) 기재 가이드 - SEO 롱테일 페이지
 * 
 * 타겟 키워드: 행발 기재, 행동특성 종합의견, 행발 예시, 행발 인성 문구, 생기부 행발
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../../components/layout/Footer';
import { NamedGrayIcon } from '../../components/icons/GrayIconBox';
import '../../styles/design-system/index.css';

const HaengbalGuidePage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('리더십');

  const categories = ['리더십', '협동심', '성실성', '창의성', '배려심', '자기주도'];

  const examples = {
    '리더십': [
      {
        title: '학급 임원으로서의 리더십',
        bad: '리더십이 좋고 학급을 잘 이끌었음.',
        good: '학급 반장으로서 청소 구역 배정을 두고 의견이 갈렸을 때 양쪽 입장을 정리해 순환제를 제안하고, 학급회의 투표로 모두가 받아들이는 결론을 이끌어 냄. 특히 학습에 어려움을 겪는 친구들을 위해 자발적으로 또래 멘토링 모임을 꾸려 매주 운영함.'
      },
      {
        title: '프로젝트 리더로서의 역할',
        bad: '조별 활동에서 리더를 맡아 잘 이끌었음.',
        good: '과학 탐구 프로젝트에서 조장을 맡아 역할 분담을 체계적으로 진행하고, 매주 진행 상황을 점검하는 회의를 주도함. 구성원들의 강점을 파악하여 적재적소에 배치하는 모습에서 뛰어난 조직력을 보임.'
      }
    ],
    '협동심': [
      {
        title: '모둠 활동에서의 협력',
        bad: '모둠 활동에서 협동심이 좋음.',
        good: '사회 탐구 모둠 활동에서 자료 조사와 발표 자료 제작을 담당하며, 다른 모둠원의 아이디어를 경청하고 통합하는 모습을 보임. 의견 충돌 상황에서 양측의 입장을 이해하고 절충안을 제시하여 원활한 협업을 이끌어냄.'
      }
    ],
    '성실성': [
      {
        title: '학습에 대한 성실한 태도',
        bad: '성실하게 학교생활을 함.',
        good: '3년간 무지각·무결석으로 출석하였으며, 수업 시간에 항상 집중하는 모습을 보임. 과제물을 성실하게 제출하고, 부족한 부분은 스스로 보충 학습을 계획하여 꾸준히 실천함. 특히 수학 성적 향상을 위해 매일 아침 자율학습 시간을 활용한 점이 인상적임.'
      }
    ],
    '창의성': [
      {
        title: '문제 해결에서의 창의적 접근',
        bad: '창의적인 학생임.',
        good: '과학 실험 수업에서 기존 방법으로 해결되지 않는 문제 상황에 직면했을 때, 새로운 실험 조건을 제안하여 성공적인 결과를 도출함. 일상적인 현상에서도 궁금증을 가지고 탐구하려는 자세가 돋보이며, 이를 발표 형식으로 정리하여 학급 친구들과 공유함.'
      }
    ],
    '배려심': [
      {
        title: '어려운 친구를 돕는 모습',
        bad: '친구들을 잘 도와줌.',
        good: '전학 온 학생이 학교생활에 적응하는 데 어려움을 겪을 때, 자발적으로 학교 시설과 수업 진도를 안내하며 곁에서 도움을 줌. 학급 내에서 소외되는 친구가 없도록 세심하게 배려하는 모습에서 따뜻한 인성을 느낄 수 있음.'
      }
    ],
    '자기주도': [
      {
        title: '진로 탐색의 자기주도성',
        bad: '자기주도적으로 공부함.',
        good: '의료 분야 진로 목표를 세운 후, 관련 도서를 스스로 찾아 읽고 독후감을 작성함. 교내 진로탐색 프로그램에 적극 참여하였으며, 방학 중에는 지역 보건소 봉사활동에 참여하여 현장 경험을 쌓음. 목표를 향해 체계적으로 계획을 세우고 실천하는 모습이 인상적임.'
      }
    ]
  };

  const writingTips = [
    { icon: 'note', title: '구체적 사례 필수', desc: '추상적 칭찬 대신 실제 행동·사건을 기술' },
    { icon: 'book', title: '스토리라인 구성', desc: '변화 과정(어려움→노력→성장)을 보여주기' },
    { icon: 'scale', title: '균형 잡힌 서술', desc: '장점 중심이되, 발전 가능성도 함께 기술' },
    { icon: 'target', title: '학생 특성 반영', desc: '다른 학생과 구별되는 개인적 특성 강조' }
  ];

  return (
    <>
      <Helmet>
        <title>행발 기재 예시 & 작성법 | 행동특성 종합의견 인성 문구 모음 (2026)</title>
        <meta name="description" content="행동특성 및 종합의견(행발) 기재 예시 모음. 리더십, 협동심, 성실성, 창의성, 배려심 인성요소별 문구. 잘못된 예시와 올바른 예시 비교. AI로 학생별 맞춤 행발 자동 생성." />
        <meta name="keywords" content="행발 기재, 행동특성 종합의견, 행발 예시, 행발 인성 문구, 생기부 행발, 행발 작성법, 행특 기재, 종합의견 예시, 행발 문구 모음" />
        <link rel="canonical" href="https://whatquiz.co.kr/guide/haengbal" />
        
        <meta name="robots" content="index, follow" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://whatquiz.co.kr/guide/haengbal" />
        <meta property="og:title" content="행발 기재 예시 & 작성법 | 인성요소별 문구 모음" />
        <meta property="og:description" content="행동특성 종합의견 인성요소별 기재 예시. 리더십, 협동심, 성실성 문구." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png?v=20260210" />
        <meta property="og:locale" content="ko_KR" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "행동특성 및 종합의견(행발) 작성하는 방법",
            "description": "학생의 인성을 잘 드러내는 행발 작성 가이드",
            "step": [
              { "@type": "HowToStep", "name": "인성요소 파악", "text": "학생의 두드러진 인성요소(리더십, 협동심 등) 파악" },
              { "@type": "HowToStep", "name": "구체적 사례 수집", "text": "해당 인성을 보여주는 구체적 행동/사건 정리" },
              { "@type": "HowToStep", "name": "변화 과정 서술", "text": "어려움→노력→성장의 스토리라인 구성" },
              { "@type": "HowToStep", "name": "긍정적 관점 유지", "text": "장점 중심으로 발전 가능성 포함하여 기술" }
            ]
          })}
        </script>
      </Helmet>

      <div style={{ 
        fontFamily: 'var(--whatquiz-font-family)',
        backgroundColor: '#fff',
        minHeight: '100vh'
      }}>
        {/* 히어로 */}
        <section style={{
          padding: '80px 20px 60px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #fef3c7 0%, #fff 100%)'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span style={{
              display: 'inline-block',
              padding: '6px 16px',
              backgroundColor: '#fde68a',
              color: '#92400e',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '16px'
            }}>
              인성요소별 문구 모음
            </span>
            
            <h1 style={{
              fontSize: 'clamp(26px, 5vw, 40px)',
              fontWeight: '800',
              color: '#111',
              lineHeight: '1.3',
              marginBottom: '20px'
            }}>
              행발 기재 예시<br />
              <span style={{ color: '#d97706' }}>& 작성법</span>
            </h1>
            
            <p style={{
              fontSize: '17px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '32px'
            }}>
              행동특성 및 종합의견<br />
              <strong>인성요소별 올바른 기재 예시</strong>
            </p>
          </div>
        </section>

        {/* 작성 팁 */}
        <section style={{
          padding: '40px 20px',
          backgroundColor: '#fffbeb'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              {writingTips.map((tip, idx) => (
                <div key={idx} style={{
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}><NamedGrayIcon name={tip.icon} boxSize={40} /></div>
                  <h4 style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#111',
                    margin: '12px 0 8px'
                  }}>{tip.title}</h4>
                  <p style={{
                    fontSize: '13px',
                    color: '#64748b',
                    margin: 0
                  }}>{tip.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 인성요소별 예시 */}
        <section style={{
          padding: '60px 20px',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#111',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            인성요소 선택
          </h2>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            justifyContent: 'center',
            marginBottom: '40px'
          }}>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: selectedCategory === cat ? '#fff' : '#374151',
                  backgroundColor: selectedCategory === cat ? '#d97706' : '#f1f5f9',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* 예시 표시 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}>
            {examples[selectedCategory]?.map((example, idx) => (
              <div key={idx} style={{
                padding: '24px',
                backgroundColor: '#f8fafc',
                borderRadius: '16px'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#111',
                  marginBottom: '20px'
                }}>
                  {example.title}
                </h3>

                {/* 잘못된 예시 */}
                <div style={{
                  padding: '16px',
                  backgroundColor: '#fef2f2',
                  borderRadius: '8px',
                  borderLeft: '4px solid #ef4444',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#dc2626',
                    marginBottom: '8px'
                  }}>
                    이렇게 쓰면 안 돼요
                  </div>
                  <p style={{
                    fontSize: '14px',
                    color: '#7f1d1d',
                    margin: 0,
                    lineHeight: '1.6'
                  }}>
                    {example.bad}
                  </p>
                </div>

                {/* 올바른 예시 */}
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f0fdf4',
                  borderRadius: '8px',
                  borderLeft: '4px solid #22c55e'
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#16a34a',
                    marginBottom: '8px'
                  }}>
                    이렇게 써야 해요
                  </div>
                  <p style={{
                    fontSize: '14px',
                    color: '#166534',
                    margin: 0,
                    lineHeight: '1.8'
                  }}>
                    {example.good}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section style={{
          padding: '80px 20px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #fff 0%, #eff6ff 100%)'
        }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#111',
              marginBottom: '16px'
            }}>
              30명 행발,<br />
              <span style={{ color: '#3182f6' }}>AI가 3분 만에</span>
            </h2>
            
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '32px'
            }}>
              학생 특성만 입력하면<br />
              <strong>인성요소 맞춤 행발 자동 생성</strong>
            </p>

            <button
              onClick={() => navigate('/create-saenggibu')}
              style={{
                padding: '16px 40px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#fff',
                backgroundColor: '#3182f6',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              AI로 행발 작성하기
            </button>
          </div>
        </section>

        {/* 관련 가이드 */}
        <section style={{
          padding: '40px 20px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111',
            marginBottom: '16px'
          }}>
            관련 가이드
          </h3>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <Link to="/guide/saenggibu-2026" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              2026 생기부 기재요령
            </Link>
            <Link to="/guide/setak-example" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              세특 예시 모음
            </Link>
            {/* 정적 HTML 페이지라 <Link> 가 아니라 <a> 로 연결한다. */}
            <a href="/saenggibu/examples/" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              행특·창체 예시 사전
            </a>
            <a href="/saenggibu/limits/haengteuk/" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              행특 글자수 300자 (2026)
            </a>
            <a href="/saenggibu/year-end-checklist/" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              학년말 마감 점검표
            </a>
            <a href="/saenggibu/rules/" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              기재 금지 사전
            </a>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default HaengbalGuidePage;
